import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthUser } from './useAuthUser';

/**
 * O que faz: Centraliza leitura e manutenção dos serviços da clínica no Supabase.
 * Onde usa: Aba Configurações > Serviços e componentes que listam serviços.
 * Conexões: useAuthUser, React Query, Supabase client, toasts de feedback.
 * Tabelas: clinica_servicos (SELECT/INSERT/UPDATE com filtros por clinica_id e ativo).
 * Data: 28/04/2026
 */
export interface ClinicaServico {
  id: string;
  clinica_id: string;
  nome_servico: string;
  ativo: boolean;
  tipo_preco?: 'fixo' | 'faixa';
  preco_fixo?: number | null;
  preco_minimo?: number | null;
  preco_maximo?: number | null;
  detalhes?: string | null;
  tempo_medio_minutos?: number;
  created_at: string;
  updated_at: string;
}

export interface AddClinicServiceInput {
  nomeServico: string;
  tipoPreco: 'fixo' | 'faixa';
  precoFixo?: number | null;
  precoMinimo?: number | null;
  precoMaximo?: number | null;
  detalhes?: string;
}

const getClinicServiceErrorMessage = (error: { code?: string; message?: string } | null) => {
  const technicalMessage = error?.message?.toLowerCase() ?? '';

  // Traduz o desalinhamento de schema que causava o erro genérico ao cadastrar preços.
  if (error?.code === '42703' || technicalMessage.includes('schema cache') || technicalMessage.includes('column')) {
    return 'O cadastro de serviços precisa de uma atualização do banco de dados. Avise o suporte.';
  }

  if (error?.code === '23505') return 'Este serviço já está cadastrado.';
  if (error?.code === '42501') return 'Você não tem permissão para cadastrar serviços nesta clínica.';
  return 'Não foi possível adicionar o serviço. Tente novamente.';
};

export const useClinicServices = (targetClinicaId?: string | null) => {
  const { userProfile } = useAuthUser();
  // A ficha administrativa informa a clínica do lead; null impede fallback para outra clínica.
  const clinicaId = targetClinicaId === undefined ? userProfile?.clinica_id : targetClinicaId;
  const queryClient = useQueryClient();

  const { data: services = [], isLoading, error } = useQuery({
    queryKey: ['clinic-services', clinicaId],
    queryFn: async (): Promise<ClinicaServico[]> => {
      // Sem escopo não existem opções seguras para apresentar no seletor.
      if (!clinicaId) return [];

      // clinica_servicos: SELECT de todos os campos dos serviços ativos da clínica escolhida.
      // Inclui preços; o filtro explícito evita misturar catálogos no admin e preserva o RLS.
      const { data, error } = await supabase
        .from('clinica_servicos')
        .select('*')
        .eq('clinica_id', clinicaId)
        .eq('ativo', true)
        .order('nome_servico');

      if (error) throw new Error('Erro ao carregar serviços da clínica');
      return data as ClinicaServico[];
    },
    enabled: !!clinicaId,
    staleTime: 5 * 60 * 1000,
  });

  const addServiceMutation = useMutation({
    mutationFn: async (input: AddClinicServiceInput): Promise<ClinicaServico> => {
      // A criação usa o mesmo escopo do catálogo para não gravar em outra clínica no modo admin.
      if (!clinicaId) throw new Error('Clínica não identificada');

      // Garante nome preenchido porque o serviço é usado em seletores de agendamento.
      const nomeServico = input.nomeServico.trim();
      if (!nomeServico) throw new Error('Nome do serviço é obrigatório');

      // Regra de negócio: em preço fixo o valor único é obrigatório para evitar cadastro incompleto.
      if (input.tipoPreco === 'fixo') {
        if (input.precoFixo === null || input.precoFixo === undefined || Number.isNaN(input.precoFixo)) {
          throw new Error('Informe o preço fixo do serviço');
        }
        if (input.precoFixo < 0) throw new Error('O preço fixo não pode ser negativo');
      }

      // Regra de negócio: em faixa precisamos de mínimo e máximo consistentes.
      if (input.tipoPreco === 'faixa') {
        if (input.precoMinimo === null || input.precoMinimo === undefined || Number.isNaN(input.precoMinimo)) {
          throw new Error('Informe o preço mínimo da faixa');
        }
        if (input.precoMaximo === null || input.precoMaximo === undefined || Number.isNaN(input.precoMaximo)) {
          throw new Error('Informe o preço máximo da faixa');
        }
        if (input.precoMinimo < 0 || input.precoMaximo < 0) {
          throw new Error('Os preços da faixa não podem ser negativos');
        }
        if (input.precoMinimo > input.precoMaximo) {
          throw new Error('O preço mínimo não pode ser maior que o máximo');
        }
      }

      // Usa a lista ativa já carregada para evitar uma segunda leitura antes do INSERT.
      // A migration também cria uma proteção única no banco contra cadastros simultâneos.
      const serviceAlreadyExists = services.some(service =>
        service.nome_servico.trim().toLocaleLowerCase('pt-BR') === nomeServico.toLocaleLowerCase('pt-BR')
      );
      if (serviceAlreadyExists) throw new Error('Este serviço já está cadastrado');

      // Tabela: clinica_servicos | Operação: INSERT | Campos: nome, tipo de preço, valores e detalhes
      const { data, error } = await supabase
        .from('clinica_servicos')
        .insert({
          clinica_id: clinicaId,
          nome_servico: nomeServico,
          tipo_preco: input.tipoPreco,
          preco_fixo: input.tipoPreco === 'fixo' ? input.precoFixo ?? null : null,
          preco_minimo: input.tipoPreco === 'faixa' ? input.precoMinimo ?? null : null,
          preco_maximo: input.tipoPreco === 'faixa' ? input.precoMaximo ?? null : null,
          detalhes: input.detalhes?.trim() ? input.detalhes.trim() : null,
          ativo: true,
        })
        .select()
        .single();

      if (error) throw new Error(getClinicServiceErrorMessage(error));
      return data as ClinicaServico;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-services', clinicaId] });
      toast.success('Serviço adicionado com sucesso!');
    },
    onError: (error: Error) => toast.error(error.message || 'Erro ao adicionar serviço'),
  });

  const removeServiceMutation = useMutation({
    mutationFn: async (serviceId: string): Promise<void> => {
      // clinica_servicos: UPDATE apenas de ativo=false, por id e clínica; preserva o histórico do serviço.
      if (!clinicaId) throw new Error('Clínica não identificada');
      const { error } = await supabase.from('clinica_servicos').update({ ativo: false }).eq('id', serviceId).eq('clinica_id', clinicaId);
      if (error) throw new Error('Erro ao remover serviço');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-services', clinicaId] });
      toast.success('Serviço removido com sucesso!');
    },
    onError: (error: Error) => toast.error(error.message || 'Erro ao remover serviço'),
  });

  const updateServiceTimingMutation = useMutation({
    mutationFn: async ({ serviceId, tempoMinutos }: { serviceId: string; tempoMinutos: number | null }): Promise<void> => {
      // clinica_servicos: UPDATE de tempo_medio_minutos limitado ao serviço da clínica selecionada.
      if (!clinicaId) throw new Error('Clínica não identificada');
      if (tempoMinutos !== null && (tempoMinutos < 5 || tempoMinutos > 480)) {
        throw new Error('Tempo deve estar entre 5 e 480 minutos');
      }
      const { error } = await supabase
        .from('clinica_servicos')
        .update({ tempo_medio_minutos: tempoMinutos })
        .eq('id', serviceId)
        .eq('clinica_id', clinicaId);
      if (error) throw new Error('Erro ao atualizar tempo do serviço');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-services', clinicaId] });
      toast.success('Tempo médio atualizado com sucesso!');
    },
    onError: (error: Error) => toast.error(error.message || 'Erro ao atualizar tempo do serviço'),
  });

  return {
    services,
    isLoading,
    error,
    addService: addServiceMutation.mutateAsync,
    removeService: removeServiceMutation.mutateAsync,
    updateServiceTiming: updateServiceTimingMutation.mutateAsync,
    isAddingService: addServiceMutation.isPending,
    isRemovingService: removeServiceMutation.isPending,
    isUpdatingTiming: updateServiceTimingMutation.isPending,
  };
};
